/** @format */

import { Injectable } from "@angular/core";
import { parseZone } from "moment-timezone";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { NsEmpWebExpData } from "../models/emp-web-exp.model";
import { ApiService } from "./api.service";
import { isMobileView } from "../functions/utils";
import { ActivatedRoute } from "@angular/router";
import { NsWISConfigurations } from "../models/configurations.model";
import MISC from "../constants/misc.constants";
import { appprovalDataListGenerator, partialCancellation } from "../functions/booking-utils.functions";
import { LoaderService } from "./loader.service";

export type TofficeCheckInMode = "directCheckIn" | "digiPass" | "scanQR" | "noCheckIn";
export interface IBookingConfiguration {
  editAllowed: boolean;
  createAllowed: boolean;
  createBookingAllowed: boolean;
  wfhCreateAllowed: boolean;
  editCutoff: string;
  cancelCutoff: string;
  conversionAllowed: {
    wfo: boolean;
    wfh: boolean;
  };
  seatEnabled: boolean;
  cabEnabled: boolean;
  generateGatepassAdvanceCutOff: number;
  generateGatepassDelayCutOff: number;
  approvalFlowEnabled?: boolean;
  showRegistrationNumberInputFieldForParking: boolean;
  loginEditCutoff: number;
  logoutEditCutoff: number;
  enableGridFloorPlan?: boolean;
  singleShiftEnabled?: boolean;
  isShuttleRequired?: boolean;
  officeCheckInModeWeb?: TofficeCheckInMode;
}
@Injectable({
  providedIn: "root"
})
export class ConfigurationService {
  constructor(private apiService: ApiService, private route: ActivatedRoute, private loader: LoaderService) {}
  isMobile: boolean = isMobileView();
  isTeamsApp = this.route.snapshot.queryParamMap.get("source") === "ms_teams";
  configurations: Observable<NsWISConfigurations.IConfigurations>;
  bookingConfigurations: Observable<IBookingConfiguration>;
  viewBookingId: string = this.route.snapshot.queryParamMap.get(MISC.VIEW_BOOKING_ID);
  editBookingId: string = this.route.snapshot.queryParamMap.get(MISC.EDIT_BOOKING_ID);

  prepareConfigsToApply(configurations: Observable<NsWISConfigurations.IConfigurations>) {
    this.bookingConfigurations = configurations.pipe(
      map(configs => {
        const bookingSettings = configs.BUSettings.features.find(x => x.id === "booking");
        const bookingConfig = {
          editAllowed: bookingSettings.editBookingEnabled,
          createAllowed: bookingSettings.createBookingEnabled,
          wfhCreateAllowed: configs.flattenedSettings.wfhBookingAllowed,
          createBookingAllowed: bookingSettings?.createBookingEnabled || configs.flattenedSettings.wfhBookingAllowed,
          editCutoff: configs.flattenedSettings.bookingEditCutOff,
          cancelCutoff: configs.flattenedSettings.employeeCancelCutOff,
          conversionAllowed: {
            wfo: configs.flattenedSettings.allowBookingConversionFromWfoToWfh && bookingSettings.editBookingEnabled,
            wfh: configs.flattenedSettings.allowBookingConversionFromWfhToWfo && bookingSettings.editBookingEnabled
          },
          seatEnabled: bookingSettings.seatBookingEnabled,
          cabEnabled: configs.flattenedSettings.showCabs,
          generateGatepassAdvanceCutOff: bookingSettings.generateGatepassAdvanceCutOff,
          generateGatepassDelayCutOff: bookingSettings.generateGatepassDelayCutOff,
          showRegistrationNumberInputFieldForParking: configs.flattenedSettings.showRegistrationNumberInputFieldForParking,
          approvalFlowEnabled: configs.flattenedSettings.approvalFlowEnabled,
          enableEmployeePreferences: configs.flattenedSettings.enableEmployeePreferences,
          autoPopulateBookingForm: configs.flattenedSettings.autoPopulateBookingForm,
          loginEditCutoff: Number(configs.flattenedSettings.employeeScheduleCutoff),
          logoutEditCutoff: Number(configs.flattenedSettings.employeeEndTimeBookingCutoff),
          enableGridFloorPlan: configs.flattenedSettings.enableGridFloorPlan,
          singleShiftEnabled: configs.flattenedSettings.singleShiftOperations,
          isShuttleRequired: configs.flattenedSettings.isShuttleRequired,
          officeCheckInModeWeb: configs.flattenedSettings.officeCheckInModeWeb
        };
        return bookingConfig;
      })
    );
    return this.bookingConfigurations;
  }

  refreshReplayStreams(): void {
    this.configurations = this.apiService.genReplayStream(this.apiService.getConfigurations());
    this.prepareConfigsToApply(this.configurations);
  }

  private getApproval(booking: NsEmpWebExpData.IBookingDetailDTOS, configs: IBookingConfiguration) {
    const approvalEnabled = configs?.approvalFlowEnabled;
    if (
      approvalEnabled &&
      !(booking.requestStatus === "SIGNED_IN" || booking.requestStatus === "SIGNED_OUT" || booking.requestStatus === "CANCELLED")
    ) {
      const list = appprovalDataListGenerator(booking);
      booking.list = { ...list };
    }
  }

  validateBooking(booking: NsEmpWebExpData.IBookingDetailDTOS, configs: IBookingConfiguration) {
    this.loader.hide();
    this.getApproval(booking, configs);
    const allowedCancellationStates = new Set<NsEmpWebExpData.IBookingStates>(["CONFIRMED", "PENDING"]);
    const allowedEditStates = new Set<NsEmpWebExpData.IBookingStates>(["CONFIRMED", "PENDING", "SIGNED_IN"]);
    const allowedCheckStates = new Set<NsEmpWebExpData.IBookingStates>(["CONFIRMED", "SIGNED_IN"]);
    const disabledActionStates = new Set<NsEmpWebExpData.IBookingStates>(["SIGNED_OUT", "CANCELLED", "REJECTED"]);
    let enableActions = true;
    if (disabledActionStates.has(booking.requestStatus)) {
      enableActions = false;
    }
    const start = parseZone(booking.startTime);
    const end = parseZone(booking.endTime);
    const now = parseZone();
    const MS_PER_MINUTE = 60000;
    const ADVANCE_MINUTES = Number(configs.generateGatepassAdvanceCutOff);
    const DELAY_MINUTES = Number(configs.generateGatepassDelayCutOff);
    const currentTime = parseZone().tz(booking.timezone);
    const configResp = {
      partiallyCancelled: partialCancellation(booking),
      editBookingAllowed:
        enableActions &&
        configs.editAllowed &&
        (start.clone().subtract(configs.loginEditCutoff, "hours").isAfter(now) ||
          end.clone().subtract(configs.logoutEditCutoff, "hours").isAfter(now)) &&
        allowedEditStates.has(booking.requestStatus),
      cancelAllowed:
        enableActions &&
        start.clone().subtract(Number(configs.cancelCutoff), "hours").isAfter(now) &&
        allowedCancellationStates.has(booking.requestStatus),
      bookingConversionAllowed:
        enableActions &&
        configs.editAllowed &&
        allowedCancellationStates.has(booking.requestStatus) &&
        start.clone().subtract(Number(configs.editCutoff), "hours").isAfter(now) &&
        ((booking.bookingRequestType === "OFFICE" && configs.wfhCreateAllowed && configs.conversionAllowed.wfo) ||
          (booking.bookingRequestType === "WFH" && configs.createAllowed && configs.conversionAllowed.wfh)),
      cabsEnabled: configs.cabEnabled,
      seatsEnabled: configs.seatEnabled,
      isCheckinDisabled:
        !(
          parseZone(booking.startTime - ADVANCE_MINUTES * MS_PER_MINUTE)
            .tz(booking.timezone)
            .isSameOrBefore(currentTime) &&
          parseZone(booking.startTime + DELAY_MINUTES * MS_PER_MINUTE)
            .tz(booking.timezone)
            .isSameOrAfter(currentTime)
        ) || !allowedCheckStates.has(booking.requestStatus),
      booking
    };
    return configResp;
  }

  // To apply global configurations
  // applyConfigurations(booking: NsEmpWebExpData.IBookingDetailDTOS): Observable<NsExpWidget.IBookingValidity> {
  //   return this.bookingConfigurations.pipe(map(configs => (!booking ? {} : this.validateBooking(booking, configs))));
  // }

  applyOfficeConfigurations(booking: NsEmpWebExpData.IBookingDetailDTOS): Observable<any> {
    return this.prepareConfigsToApply(this.apiService.getConfigurations(booking?.officeId)).pipe(
      map(configs => (!booking ? {} : this.validateBooking(booking, configs)))
    );
  }
}
